import React from "react";
import OfficeToHomeWarning, { officeToHomeConflictMessage } from "./office_to_home_warning";
import OfferedPatientsTooltip from "./offered_patients_tooltip";
import { selectedSlotFromEntry, type ConsultAvailabilityDate, type SelectedConsultSlot } from "./consult_availability";

interface Props {
  visibleDates: ConsultAvailabilityDate[];
  selectedSlotKeys: string[];
  selectedSlots: SelectedConsultSlot[];
  loadingAvailability: boolean;
  showBookedAppointments: boolean;
  emptyMessage: string;
  handleDateSelection: (date: ConsultAvailabilityDate, checked: boolean) => void;
  handleSlotToggle: (slot: SelectedConsultSlot, checked: boolean) => void;
}

export default function ClassicConsultAvailability({ visibleDates, selectedSlotKeys, selectedSlots, loadingAvailability, showBookedAppointments, emptyMessage, handleDateSelection, handleSlotToggle }: Props) {
  return (
    visibleDates.length > 0 ? (
      <div className="mt-4 space-y-4">
        {visibleDates.map((date) => (
          <section key={date.date} className="rounded-xl border border-neutral-200/80 p-4 dark:border-neutral-800">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-950 dark:text-white">{date.label}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="app-button-secondary px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={`Select all available times for ${date.label}`}
                    disabled={loadingAvailability || date.entries.every((entry) => !entry.selectable || officeToHomeConflictMessage(entry) || selectedSlotKeys.includes(entry.key))}
                    onClick={() => handleDateSelection(date, true)}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="app-button-secondary px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={`Deselect all available times for ${date.label}`}
                    disabled={loadingAvailability || !date.entries.some((entry) => entry.selectable && selectedSlotKeys.includes(entry.key))}
                    onClick={() => handleDateSelection(date, false)}
                  >
                    Deselect all
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="app-badge border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/60 dark:bg-sky-900/30 dark:text-sky-300">
                  {date.openSlotCountLabel}
                </span>
                {showBookedAppointments && date.hasBookedSlots ? (
                  <span className="app-badge border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-900/30 dark:text-amber-300">
                    {date.bookedSlotCountLabel}
                  </span>
                ) : null}
              </div>
            </div>

            <OfficeToHomeWarning slots={selectedSlots.filter((slot) => slot.date === date.date)} />

            <div className="mt-4 grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
              {date.entries.map((entry) =>
                entry.selectable ? (
                  <div key={entry.key} className="app-check-card">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500 dark:border-neutral-700 dark:bg-neutral-900"
                        checked={selectedSlotKeys.includes(entry.key)}
                        onChange={(event) => handleSlotToggle(selectedSlotFromEntry(date, entry), event.target.checked)}
                      />
                      <span>
                        <span className="block font-semibold text-neutral-950 dark:text-white">{entry.scheduleLabel}</span>
                        <span className="mt-1 block text-xs uppercase tracking-[0.16em] text-neutral-500 dark:text-neutral-400">
                          {entry.devaLocationLabel}
                        </span>
                      </span>
                    </label>
                    <OfferedPatientsTooltip patients={entry.offeredPatients} scheduleLabel={entry.scheduleLabel} />
                  </div>
                ) : (
                  <div
                    key={entry.key}
                    className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-100"
                  >
                    <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-amber-300 bg-amber-100 text-[10px] font-semibold text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
                      -
                    </span>
                    <span>
                      <span className="block font-semibold">{entry.scheduleLabel}</span>
                      <span className="mt-1 block text-xs uppercase tracking-[0.16em] text-amber-700/80 dark:text-amber-300/80">
                        {entry.devaLocationLabel}
                      </span>
                      <span className="mt-2 block text-sm">
                        <a
                          href={entry.bookedPatientPath ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold hover:text-amber-700 dark:hover:text-amber-200"
                        >
                          {entry.bookedPatientName}
                        </a>
                        {" / "}
                        <a
                          href={entry.bookedConsultPath ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold hover:text-amber-700 dark:hover:text-amber-200"
                        >
                          {entry.bookedConsultTypeLabel}
                        </a>
                      </span>
                    </span>
                  </div>
                ),
              )}
            </div>
          </section>
        ))}
      </div>
    ) : (
      <p className="mt-4 rounded-xl border border-dashed border-neutral-300 px-4 py-5 text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
        {emptyMessage}
      </p>
    )
  );
}
